import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTruck, faStore, faUser, faMapMarkerAlt, faMap, faCheckCircle, faTimesCircle, faBox, faPallet, faMotorcycle, faHandHolding, faBolt, faClock } from '@fortawesome/free-solid-svg-icons';
import { UserService } from '../../../_services/user.service';
import { ShippingConfigService } from '../../../_services/shipping-config.service';
import {
  ArticleDisposition,
  PackageSpeed,
  PickpointStation,
  PickpointStationShort,
  ShipmentMethod,
  ShippingConfig,
  ShippingConfigContext,
  ShippingConfigFormData,
  ShippingConfigType,
} from '../../../_models/shipping-config.interface';
import { PickpointMapPickerComponent } from './pickpoint-map-picker.component';

interface TechnicianOption { id: number; name: string; }
interface WarehouseOption { id: number; label: string; }

/**
 * Pure Formular-Komponente fuer das Erstellen/Bearbeiten einer Versandkonfiguration.
 *
 * Hat keine eigene HTTP-Persistenz: Context wird vom Parent als Input geliefert,
 * die Form emittiert beim Speichern die Datenform `ShippingConfigFormData`. Der
 * Parent ist fuer das HTTP-POST zustaendig (Single-Op-API, Bulk-API oder
 * beliebigen anderen Endpunkt).
 *
 * Techniker-Liste und Techniker-Lager werden intern ueber den `UserService`
 * geladen — diese Listen haengen vom `projectId`- bzw. `targetUserId`-Input ab
 * und sind in allen Einsatzkontexten gleich.
 */
@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, FontAwesomeModule, PickpointMapPickerComponent],
  selector: 'hawk-shipping-config-form',
  templateUrl: './shipping-config-form.component.html',
})
export class ShippingConfigFormComponent implements OnInit, OnChanges {
  /** Bestehende Config fuer Edit-Modus; null oder undefined fuer Create-Modus. */
  @Input() initialConfig: ShippingConfig | null = null;
  /** Kontextinfos (Rueckadresse-Flag, Filialname) — vom Parent geladen. */
  @Input() context: ShippingConfigContext | null = null;
  /** Store-ID des Tickets — entscheidet ob `store_shipping` erlaubt ist. */
  @Input() ticketStoreId?: number;
  /** Fuer den PickPoint-Kartenpicker: Mittelpunkt. */
  @Input() ticketLat?: number;
  @Input() ticketLng?: number;
  /** Stichtag fuer Station-Aktivitaet (optional). */
  @Input() referenceDate?: string;
  /** Project-ID zum Filtern der Technikerliste (optional). */
  @Input() projectId?: number;
  /** Vorausgewaehlter Techniker (z.B. Operations-Techniker bei Neu-Anlage). */
  @Input() operationTechnicianId?: number;
  /** Ueberschrift des Cards. `null` blendet sie aus. */
  @Input() heading: string | null = 'Versandkonfiguration';
  /** Primaer-Button-Label. */
  @Input() saveLabel = 'Speichern';
  /** Hinweistext unter dem Versanddatum. */
  @Input() shippingDateHint = 'Leer lassen für Versand am Auftragsdatum.';

  @Output() save = new EventEmitter<ShippingConfigFormData>();
  @Output() cancel = new EventEmitter<void>();

  // Icons
  iconMap = faMap;
  iconCheck = faCheckCircle;
  iconTimes = faTimesCircle;

  // State
  showMapPicker = signal(false);
  selectedType = signal<ShippingConfigType>('pickpoint');
  selectedStation = signal<PickpointStationShort | null>(null);
  shipmentMethod = signal<ShipmentMethod | null>('parcel');
  packageSpeed = signal<PackageSpeed | null>('standard');
  articleDisposition = signal<ArticleDisposition>('technician_transfer');
  shippingDate = signal<string>('');

  technicians = signal<TechnicianOption[]>([]);
  technicianWarehouses = signal<WarehouseOption[]>([]);
  loadingWarehouses = signal(false);

  // Signals fuer Felder, die in `canSave` einfliessen — sonst triggert
  // ngModel-Tippen kein Recompute (Button bleibt disabled).
  targetUserId = signal<number | null>(null);
  pmCode = signal('');

  // Form-Felder ohne Einfluss auf `canSave` (direkt mit [(ngModel)] gebunden)
  targetWarehouseId: number | null = null;
  configNotes = '';

  // Erweiterter Bereich (collapsible). Enthaelt Ziel-Techniker, Ziel-Lager,
  // Notizen und ggf. den PM-Code (sofern er erfolgreich vom System-User
  // vorbelegt werden konnte). Default zugeklappt — beim Bearbeiten einer
  // bestehenden Config wird automatisch ausgeklappt, wenn dort Werte stehen.
  showAdvancedSection = signal<boolean>(false);

  // True, wenn der PM-Code aus dem System-User (ID 1) erfolgreich vorbelegt
  // wurde — dann landet der PM-Code im erweiterten Bereich, weil er nur
  // selten manuell ueberschrieben werden muss. Andernfalls bleibt er sichtbar
  // (und Pflicht), damit der User ihn manuell eintragen kann.
  pmCodeAutoLoaded = signal<boolean>(false);

  canOpenMap = computed(() =>
    this.ticketLat != null && this.ticketLng != null &&
    !Number.isNaN(this.ticketLat) && !Number.isNaN(this.ticketLng),
  );

  showTechnicianSelector = computed(() => {
    const type = this.selectedType();
    if (type === 'pickpoint' || type === 'technician_shipping') return true;
    if (type === 'store_shipping') return this.articleDisposition() === 'technician_transfer';
    return false;
  });

  canSave = computed(() => {
    if (this.shipmentMethod() == null) return false;
    if (this.shipmentMethod() === 'parcel' && this.packageSpeed() == null) return false;

    // PM-Code ist Pflicht bei PickPoint.
    if (this.selectedType() === 'pickpoint' && !this.pmCode().trim()) return false;

    switch (this.selectedType()) {
      case 'pickpoint':
        return this.selectedStation() != null && this.targetUserId() != null;
      case 'store_shipping':
        if (this.ticketStoreId == null) return false;
        return this.articleDisposition() === 'consumed' || this.targetUserId() != null;
      case 'technician_shipping':
        return this.targetUserId() != null;
      case 'customer_shipping':
        return this.context?.customer_has_return_address === true;
      default: return false;
    }
  });

  visibleShippingTypes = computed(() => this.shippingTypes);

  shippingTypes = [
    { value: 'pickpoint' as ShippingConfigType, label: 'PickPoint', icon: faMapMarkerAlt },
    { value: 'store_shipping' as ShippingConfigType, label: 'Filialversand', icon: faStore },
    { value: 'technician_shipping' as ShippingConfigType, label: 'Technikerversand', icon: faTruck },
    { value: 'customer_shipping' as ShippingConfigType, label: 'Kundenversand', icon: faUser },
  ];

  shipmentMethods = [
    { value: 'parcel' as ShipmentMethod, label: 'Paket', icon: faBox },
    { value: 'pallet' as ShipmentMethod, label: 'Palette', icon: faPallet },
    { value: 'courier' as ShipmentMethod, label: 'Kurier', icon: faMotorcycle },
    { value: 'pickup' as ShipmentMethod, label: 'Abholung', icon: faHandHolding },
  ];

  // PickPoint-Versand: Palette und Abholung sind nicht erlaubt — die Station
  // nimmt nur Pakete und Kurier-Sendungen entgegen.
  visibleShipmentMethods = computed(() => {
    if (this.selectedType() === 'pickpoint') {
      return this.shipmentMethods.filter((m) => m.value === 'parcel' || m.value === 'courier');
    }
    return this.shipmentMethods;
  });

  packageSpeeds = [
    { value: 'standard' as PackageSpeed, label: 'Standard', icon: faClock },
    { value: 'express' as PackageSpeed, label: 'Express', icon: faBolt },
  ];

  constructor(
    private userService: UserService,
    private shippingConfigService: ShippingConfigService,
  ) {}

  ngOnInit(): void {
    this.applyInitialConfig();
    this.loadTechnicians();
    const uid = this.targetUserId();
    if (uid != null) {
      this.loadWarehouses(uid);
    }
    // PM-Code aus System-User (ID 1) vorbelegen, sofern PickPoint aktiv ist
    // und der Code noch leer ist (neue Config oder bestehende Config ohne
    // gesetzten PM-Code).
    if (this.selectedType() === 'pickpoint' && !this.pmCode().trim()) {
      this.loadMasterPmCode();
    }
    // Erweiterte Einstellungen sind grundsaetzlich eingeklappt — der User
    // klickt aktiv darauf, wenn er sie braucht.
  }

  private async loadMasterPmCode(): Promise<void> {
    const code = await this.shippingConfigService.getMasterPmCode();
    if (code) {
      this.pmCode.set(code);
      this.pmCodeAutoLoaded.set(true);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialConfig'] && !changes['initialConfig'].firstChange) {
      this.applyInitialConfig();
      const uid = this.targetUserId();
      if (uid != null) {
        this.loadWarehouses(uid);
      }
    }
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      // Techniker-Liste neu laden (Projekt-Filter)
      this.technicians.set([]);
      this.loadTechnicians();
    }
  }

  isTypeDisabled(type: ShippingConfigType): boolean {
    return type === 'store_shipping' && this.ticketStoreId == null;
  }

  typeDisabledReason(type: ShippingConfigType): string | null {
    if (type === 'store_shipping' && this.ticketStoreId == null) {
      return 'Nur mit Ticketfiliale möglich';
    }
    return null;
  }

  onTechnicianChange(userId: number | null): void {
    this.targetUserId.set(userId);
    this.targetWarehouseId = null;
    if (userId != null) {
      this.loadWarehouses(userId);
    } else {
      this.technicianWarehouses.set([]);
    }
  }

  setShipmentMethod(method: ShipmentMethod): void {
    this.shipmentMethod.set(method);
    if (method === 'parcel') {
      if (this.packageSpeed() == null) this.packageSpeed.set('standard');
    } else {
      this.packageSpeed.set(null);
    }
  }

  // Wechselt den Versandtyp und setzt unzulaessige Versandarten zurueck —
  // PickPoint erlaubt nur Paket und Kurier; bei Wechsel von z.B. pallet
  // auf pickpoint waere shipmentMethod sonst inkonsistent.
  // Beim Wechsel zu PickPoint wird ausserdem der Master-PM-Code geladen,
  // sofern das Feld leer ist (z.B. weil vorher eine andere Config-Variante
  // ohne PM-Code aktiv war).
  setShippingType(type: ShippingConfigType): void {
    if (this.isTypeDisabled(type)) return;
    this.selectedType.set(type);
    if (type === 'pickpoint') {
      const m = this.shipmentMethod();
      if (m !== 'parcel' && m !== 'courier') {
        this.setShipmentMethod('parcel');
      }
      if (!this.pmCode().trim()) {
        this.loadMasterPmCode();
      }
    }
  }

  onMapStationPicked(station: PickpointStation | null): void {
    if (!station) return;
    this.selectedStation.set({
      id: station.id,
      pp_id: station.pp_id,
      name: station.name,
      address_zip: station.address_zip,
      address_city: station.address_city,
      active: station.active,
    });
  }

  emitSave(): void {
    const type = this.selectedType();
    const disposition = this.defaultDispositionForType(type, this.articleDisposition());

    const data: ShippingConfigFormData = {
      shipping_type: type,
      shipment_method: this.shipmentMethod()!,
      package_speed: this.shipmentMethod() === 'parcel' ? this.packageSpeed() : null,
      article_disposition: disposition,
      shipping_date: this.shippingDate() || null,
      notes: this.configNotes || undefined,
      pm_code: type === 'pickpoint' ? this.pmCode().trim() : null,
    };

    switch (type) {
      case 'pickpoint':
        data.pickpoint_station_id = this.selectedStation()!.id;
        data.target_user_id = this.targetUserId();
        data.target_warehouse_id = this.targetWarehouseId;
        break;
      case 'store_shipping':
        if (disposition === 'consumed') {
          data.target_user_id = null;
          data.target_warehouse_id = null;
        } else {
          data.target_user_id = this.targetUserId();
          data.target_warehouse_id = this.targetWarehouseId;
        }
        break;
      case 'technician_shipping':
        data.target_user_id = this.targetUserId();
        data.target_warehouse_id = this.targetWarehouseId;
        break;
      case 'customer_shipping':
        // Keine weiteren Felder — Adresse kommt aus dem Kundenprofil.
        break;
    }

    this.save.emit(data);
  }

  private applyInitialConfig(): void {
    const c = this.initialConfig;
    if (c) {
      this.selectedType.set(c.shipping_type);
      this.selectedStation.set(c.pickpoint_station ?? null);
      // Beim Bearbeiten exakt den DB-Stand uebernehmen — KEIN Fallback auf den
      // Auftragstechniker, sonst sieht es so aus, als waere er gespeichert.
      // Auto-Configs (z.B. fuer interne Auftraege) duerfen ja explizit ohne
      // festen Techniker existieren.
      this.targetUserId.set(c.target_user_id ?? null);
      this.targetWarehouseId = c.target_warehouse_id ?? null;
      this.shipmentMethod.set(c.shipment_method ?? 'parcel');
      this.packageSpeed.set(c.shipment_method === 'parcel' ? (c.package_speed ?? 'standard') : null);
      this.articleDisposition.set(c.article_disposition ?? this.defaultDispositionForType(c.shipping_type));
      this.shippingDate.set(c.shipping_date ?? '');
      this.configNotes = c.notes ?? '';
      this.pmCode.set(c.pm_code ?? '');
    } else {
      this.selectedType.set('pickpoint');
      this.selectedStation.set(null);
      this.targetUserId.set(this.operationTechnicianId ?? null);
      this.targetWarehouseId = null;
      this.shipmentMethod.set('parcel');
      this.packageSpeed.set('standard');
      this.articleDisposition.set('technician_transfer');
      this.shippingDate.set('');
      this.configNotes = '';
      this.pmCode.set('');
    }
  }

  private loadTechnicians(): void {
    if (this.technicians().length > 0) return;
    this.userService.getTechnicians(this.projectId ? { project_id: this.projectId } : {}).subscribe({
      next: (data: any[]) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        const mapped = (data || []).map(u => ({
          id: u.id,
          name: u.full_name || u.name || `#${u.id}`,
        } as TechnicianOption));
        mapped.sort((a, b) => a.name.localeCompare(b.name));
        this.technicians.set(mapped);
      },
      error: err => console.error('Techniker konnten nicht geladen werden', err),
    });
  }

  private loadWarehouses(userId: number): void {
    this.loadingWarehouses.set(true);
    this.userService.getUsersWarehouses(userId).subscribe({
      next: (data: any[]) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        const mapped = (data || []).map(w => ({ id: w.id, label: w.label } as WarehouseOption));
        this.technicianWarehouses.set(mapped);

        const current = this.targetWarehouseId;
        if (current != null && !mapped.some(w => w.id === current)) {
          this.targetWarehouseId = mapped[0]?.id ?? null;
        } else if (current == null && mapped.length === 1) {
          this.targetWarehouseId = mapped[0].id;
        }
        this.loadingWarehouses.set(false);
      },
      error: err => {
        console.error('Lager konnten nicht geladen werden', err);
        this.technicianWarehouses.set([]);
        this.loadingWarehouses.set(false);
      },
    });
  }

  private defaultDispositionForType(
    type: ShippingConfigType,
    userChoice?: ArticleDisposition,
  ): ArticleDisposition {
    switch (type) {
      case 'customer_shipping': return 'consumed';
      case 'pickpoint':
      case 'technician_shipping': return 'technician_transfer';
      case 'store_shipping': return userChoice ?? 'technician_transfer';
    }
  }
}
